import { useEffect, useState, useCallback, useRef } from 'react';
import { View, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { Appbar, Card, Text, TextInput, Chip, ActivityIndicator, List, useTheme, Surface, Button, Checkbox, Dialog, Portal } from 'react-native-paper';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import Logo from '../../../src/components/Logo';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import { useFocusEffect } from '@react-navigation/native';
import { API_ENDPOINTS } from '../../../src/api/config';
import apiService from '../../../src/api/apiService';
import type { FundingDisplay } from '../../../src/api/apiService';
import { TransferModal } from '../../../src/components/TransferModal';
import { DriverFundingSummary } from '../../../src/components/DriverFundingSummary';
import { transferDriver, bulkActivateDrivers, bulkTransferDrivers, resendDriverInvitation, cancelDriverInvitation, getActiveCompanyContextId } from '../../../src/api/apiService';
import { InviteDriverModal } from '../../../src/components/InviteDriverModal';

type DriverRow = {
  type?: 'driver' | 'invitation';
  token?: string;
  license_payer?: 'company' | 'self';
  created_at?: string;
  expires_at?: string;
  phone_number?: string | null;
  license_number?: string | null;
  id: number;
  name: string;
  email: string;
  status: 'active' | 'inactive';
  vehicles: Array<{ id: number; registration_number: string }>;
  role?: string;
  company_nipc?: string;
  registered_at?: string;
  activated_at?: string;
  deactivated_at?: string;
  has_ever_been_activated?: boolean;
  report_hidden?: boolean;
  relationship_status?: string | null;
  funding?: FundingDisplay;
};

type PaginatedResponse = {
  results: DriverRow[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next: boolean;
  previous: boolean;
  active_count: number;
  inactive_count: number;
  awaiting_count?: number;
  pending_count?: number;
  new_count?: number;
  deactivated_count?: number;
  invited_count?: number;
  funding_counts?: Record<string, number>;
  coverage_counts?: Record<string, number>;
  license_issue_count?: number;
  capacity?: {
    purchased: number;
    used: number;
    reserved: number;
    available: number;
    drivers: Record<string, number>;
  };
};

export default function DriversListScreen() {
  const { t } = useTranslation('manageDrivers');
  const router = useRouter();
  const theme = useTheme();
  const { width, height } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { handleLogout, userData } = useUser();
  const { hasOwner, hasDriver, selectedProfile, updateProfile } = useProfile(userData?.groups || []);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'awaiting' | 'pending' | 'deactivated' | 'invited'>('all');
  const [fundingFilter, setFundingFilter] = useState<'all' | 'company' | 'self' | 'ambiguous' | 'uncovered'>('all');
  const [coverageFilter, setCoverageFilter] = useState<'all' | 'not_covered' | 'payment_issue'>('all');
  const [includeHidden, setIncludeHidden] = useState(false);
  const [orderDesc, setOrderDesc] = useState(true);
  const [sortBy, setSortBy] = useState<'registered_at' | 'name'>('registered_at');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [page, setPage] = useState(1); // Server uses 1-based pagination
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [allCount, setAllCount] = useState(0); // Preserve total count for "All" filter
  const [awaitingCount, setAwaitingCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [deactivatedCount, setDeactivatedCount] = useState(0);
  const [invitedCount, setInvitedCount] = useState(0);
  const [fundingCounts, setFundingCounts] = useState<Record<string, number>>({});
  const [coverageCounts, setCoverageCounts] = useState<Record<string, number>>({});
  const [licenseIssueCount, setLicenseIssueCount] = useState(0);
  const [selectedDriverIds, setSelectedDriverIds] = useState<Set<number>>(new Set());
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [bulkActivating, setBulkActivating] = useState(false);
  const [bulkTransferring, setBulkTransferring] = useState(false);
  const [resultDialogVisible, setResultDialogVisible] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [hasLicenseError, setHasLicenseError] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [invitationActionLoading, setInvitationActionLoading] = useState<string | null>(null);
  const [invitationActionError, setInvitationActionError] = useState<string | null>(null);
  const latestDriversRequest = useRef(0);

  const fetchDrivers = useCallback(async (fresh = false) => {
    const requestId = ++latestDriversRequest.current;
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        include_invitations: 'true',
      });

      const orderingField = sortBy === 'registered_at' ? 'registered_at' : 'user_full_name';
      params.append('ordering', `${orderDesc ? '-' : ''}${orderingField}`);

      // Add status filter - backend now supports 'active', 'inactive', 'new', 'deactivated'
      if (filter !== 'all') {
        params.append('status', filter);
      }
      if (fundingFilter !== 'all') {
        params.append('funding', fundingFilter);
      }
      if (coverageFilter !== 'all') {
        params.append('coverage', coverageFilter);
      }
      if (includeHidden) {
        params.append('include_hidden', 'true');
      }

      // Add search query if present
      if (query.trim()) {
        params.append('search', query.trim());
      }

      const url = `${API_ENDPOINTS.COMPANY_DRIVERS}?${params.toString()}`;
      const resp = await apiService.get(url, fresh ? { fresh: true } : undefined);
      if (requestId !== latestDriversRequest.current) return;
      
      const responseData = resp.data as PaginatedResponse;
      
      // Map the API response to DriverRow format
      const data = (responseData.results || []).map((emp: any) => ({
        id: emp.id,
        type: emp.type || 'driver',
        name: emp.name || emp.user_full_name || emp.email || emp.user_email,
        email: emp.email || emp.user_email,
        status: emp.is_active ? 'active' : 'inactive',
        vehicles: emp.vehicles || [],
        token: emp.token,
        license_payer: emp.license_payer,
        created_at: emp.created_at,
        expires_at: emp.expires_at,
        phone_number: emp.phone_number,
        license_number: emp.license_number,
        role: emp.role,
        company_nipc: emp.company_nipc,
        registered_at: emp.registered_at,
        activated_at: emp.activated_at,
        deactivated_at: emp.deactivated_at,
        has_ever_been_activated: emp.has_ever_been_activated || false,
        report_hidden: emp.report_hidden || false,
        relationship_status: emp.relationship_status,
        funding: emp.funding,
      })) as DriverRow[];

      setDrivers(data);
      setTotalPages(responseData.total_pages || 1);
      setActiveCount(responseData.active_count || 0);
      setInactiveCount(responseData.inactive_count || 0);
      setAwaitingCount(responseData.awaiting_count ?? 0);
      setPendingCount(responseData.pending_count ?? 0);
      setDeactivatedCount(responseData.deactivated_count ?? 0);
      setInvitedCount(responseData.invited_count ?? 0);
      setFundingCounts(responseData.funding_counts ?? {});
      setCoverageCounts(responseData.coverage_counts ?? {});
      setLicenseIssueCount(responseData.license_issue_count ?? 0);
      setTotalCount(responseData.count || 0);
      
      // Preserve the total count when viewing "all" without search query
      // This ensures the "All" chip always shows the correct total count, regardless of current filter
      if (filter === 'all' && coverageFilter === 'all' && !query.trim()) {
        setAllCount(responseData.count || 0);
      }
    } catch (error) {
      if (requestId !== latestDriversRequest.current) return;
      console.error('Error fetching drivers:', error);
      setDrivers([]);
      setTotalPages(1);
    } finally {
      if (requestId === latestDriversRequest.current) setLoading(false);
    }
  }, [page, pageSize, filter, fundingFilter, coverageFilter, includeHidden, query, orderDesc, sortBy]);

  // Fetch drivers when filters, page, or ordering changes
  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  // Reset to page 1 when filter, query, or ordering changes (but not on initial mount)
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [filter, fundingFilter, coverageFilter, includeHidden, query, orderDesc, sortBy]);

  // Refresh drivers list when screen comes into focus (e.g., when navigating back from detail page)
  useFocusEffect(
    useCallback(() => {
      fetchDrivers();
    }, [fetchDrivers])
  );

  // Selection handlers
  const toggleDriverSelection = (driverId: number) => {
    setSelectedDriverIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(driverId)) {
        newSet.delete(driverId);
      } else {
        newSet.add(driverId);
      }
      return newSet;
    });
  };

  // Fetch all drivers matching current filters (for select all)
  const fetchAllMatchingDrivers = useCallback(async (): Promise<number[]> => {
    try {
      // Fetch all pages if needed
      const allIds: number[] = [];
      let currentPage = 1;
      let hasMore = true;
      const pageSize = 100; // Use reasonable page size
      
      while (hasMore) {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          page_size: pageSize.toString(),
          include_invitations: 'true',
        });

        const orderingField = sortBy === 'registered_at' ? 'registered_at' : 'user_full_name';
        params.append('ordering', `${orderDesc ? '-' : ''}${orderingField}`);

        if (filter !== 'all') {
          params.append('status', filter);
        }
        if (fundingFilter !== 'all') {
          params.append('funding', fundingFilter);
        }
        if (coverageFilter !== 'all') {
          params.append('coverage', coverageFilter);
        }
        if (includeHidden) {
          params.append('include_hidden', 'true');
        }

        if (query.trim()) {
          params.append('search', query.trim());
        }

        const url = `${API_ENDPOINTS.COMPANY_DRIVERS}?${params.toString()}`;
        const resp = await apiService.get(url);
        
        const responseData = resp.data as PaginatedResponse;
        const pageIds = (responseData.results || [])
          .filter((row: any) => row.type !== 'invitation')
          .map((emp: any) => emp.id);
        allIds.push(...pageIds);
        
        // Check if there are more pages
        hasMore = responseData.next === true && (responseData.results || []).length === pageSize;
        currentPage++;
      }
      
      return allIds;
    } catch (error) {
      console.error('Error fetching all drivers:', error);
      // Fallback to current page if fetch fails
      return drivers.map(d => d.id);
    }
  }, [filter, fundingFilter, coverageFilter, includeHidden, query, orderDesc, sortBy, drivers]);

  const toggleSelectAll = async () => {
    // Check if all matching items are selected
    const selectableDrivers = drivers.filter(driver => driver.type !== 'invitation');
    const allCurrentPageSelected = selectableDrivers.length > 0 && selectableDrivers.every(d => selectedDriverIds.has(d.id));
    const selectableCount = filter === 'all'
      ? Math.max(0, totalCount - invitedCount)
      : filter === 'invited' ? 0 : totalCount;
    const allMatchingSelected = allCurrentPageSelected && selectedDriverIds.size >= selectableCount;
    
    if (allMatchingSelected) {
      // Deselect all
      setSelectedDriverIds(new Set());
    } else {
      // Select all matching drivers (across all pages)
      const allMatchingIds = await fetchAllMatchingDrivers();
      setSelectedDriverIds(new Set(allMatchingIds));
    }
  };

  const clearSelection = () => {
    setSelectedDriverIds(new Set());
  };

  // Calculate related vehicles count for selected drivers
  const getRelatedVehiclesCount = () => {
    let count = 0;
    selectedDriverIds.forEach(id => {
      const driver = drivers.find(d => d.id === id);
      if (driver) {
        count += driver.vehicles.length;
      }
    });
    return count;
  };

  // Bulk activate handlers
  const handleBulkActivate = async () => {
    const employmentIds = Array.from(selectedDriverIds);
    
    if (employmentIds.length === 0) {
      setResultMessage(t('bulkActions.noDriversSelected', 'No drivers selected'));
      setResultDialogVisible(true);
      return;
    }

    setBulkActivating(true);
    try {
      // Send all selected IDs - backend will filter to only inactive/new drivers
      const response = await bulkActivateDrivers({ 
        employment_ids: employmentIds 
      });
      
      if (response.ok) {
        const data = response.data as any;
        const successCount = data.success_count || 0;
        const errorCount = data.error_count || 0;
        
        // Check if there are license-related errors
        const hasLicenseErrors = data.errors && Array.isArray(data.errors) && 
          data.errors.some((err: any) => err.error && (err.error.includes('license') || err.error.includes('purchase')));
        
        setHasLicenseError(hasLicenseErrors);
        
        let message = t('bulkActions.activateResult', 'Activated {{success}} driver(s). {{errors}} error(s).', {
          success: successCount,
          errors: errorCount
        });
        
        if (hasLicenseErrors) {
          message += '\n\n' + t('bulkActions.licenseError', 'You need to purchase licenses to activate drivers.');
        }
        
        setResultMessage(message);
        setResultDialogVisible(true);
        clearSelection();
        fetchDrivers();
      } else {
        const errorData = response.data as any;
        const isLicenseError = errorData?.error?.includes('license') || errorData?.detail?.includes('license') || errorData?.error?.includes('purchase');
        setHasLicenseError(isLicenseError);
        
        let errorMsg = t('bulkActions.activateError', 'Error activating drivers');
        if (isLicenseError) {
          errorMsg = t('bulkActions.licenseError', 'You need to purchase licenses to activate drivers.');
        }
        setResultMessage(errorMsg);
        setResultDialogVisible(true);
      }
    } catch (e: any) {
      const isLicenseError = e?.message?.includes('license') || e?.message?.includes('purchase');
      setHasLicenseError(isLicenseError);
      
      let errorMsg = t('bulkActions.activateError', 'Error activating drivers: {{error}}', { error: e.message });
      if (isLicenseError) {
        errorMsg = t('bulkActions.licenseError', 'You need to purchase licenses to activate drivers.');
      }
      setResultMessage(errorMsg);
      setResultDialogVisible(true);
    } finally {
      setBulkActivating(false);
    }
  };

  // Bulk transfer handler
  const handleBulkTransfer = async (targetCompanyId: number, transferRelatedVehicles: boolean) => {
    setBulkTransferring(true);
    const employmentIds = Array.from(selectedDriverIds);

    try {
      const response = await bulkTransferDrivers({
        employment_ids: employmentIds,
        target_company_id: targetCompanyId,
        transfer_related_vehicles: transferRelatedVehicles
      });

      if (response.ok && response.data) {
        const { success_count, error_count, errors } = response.data;
        
        let errorMessages: string[] = [];
        let hasLicenseErrors = false;
        if (errors && errors.length > 0) {
          errorMessages = errors.slice(0, 5).map(err => {
            const errorText = String(err.error || '');
            if (errorText.includes('license') || errorText.includes('licenses')) {
              hasLicenseErrors = true;
            }
            return `${err.employment_id}: ${err.error}`;
          });
        }

        setHasLicenseError(hasLicenseErrors);
        const baseMessage = t('bulkActions.transferResult', 'Transferred {{success}} driver(s). {{errors}} error(s).', {
          success: success_count,
          errors: error_count
        });
        const licenseMessage = hasLicenseErrors
          ? `\n\n${t('bulkActions.transferLicenseError', 'Target company has no available driver licenses.')}`
          : '';

        setResultMessage(
          baseMessage + licenseMessage + (errorMessages.length > 0 
            ? `\n\nErrors:\n${errorMessages.join('\n')}${errors && errors.length > 5 ? `\n... and ${errors.length - 5} more` : ''}` 
            : '')
        );
      } else {
        const errorData = response.data as any;
        const isLicenseError = errorData?.license_error || errorData?.error === 'license_required' || errorData?.detail?.includes('license');
        setHasLicenseError(!!isLicenseError);
        const errorMsg = errorData?.error || errorData?.detail || 'Unknown error';
        setResultMessage(
          isLicenseError
            ? t('bulkActions.transferLicenseError', 'Target company has no available driver licenses.')
            : t('bulkActions.transferError', 'Error transferring drivers: {{error}}', { error: errorMsg })
        );
      }
      
      setResultDialogVisible(true);
      clearSelection();
      fetchDrivers();
    } catch (e: any) {
      const isLicenseError = e?.message?.includes('license') || e?.message?.includes('licenses');
      setHasLicenseError(!!isLicenseError);
      setResultMessage(
        isLicenseError
          ? t('bulkActions.transferLicenseError', 'Target company has no available driver licenses.')
          : t('bulkActions.transferError', 'Error transferring drivers: {{error}}', { error: e.message })
      );
      setResultDialogVisible(true);
    } finally {
      setBulkTransferring(false);
      setTransferModalVisible(false);
    }
  };

  const renderHeader = () => {
    if (isLargeScreen) return null;
    return (
      <Appbar.Header style={{ 
        height: height * 0.1, 
        backgroundColor: theme.colors.surface,
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} style={{ maxWidth: Platform.OS === 'web' ? 200 : 250 }} />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      {isLargeScreen && (
        <Sidebar isOpen={true} onClose={() => {}} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
      )}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(isLargeScreen ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
      <ScrollView style={{ flex: 1, padding: 12, ...(isLargeScreen ? {} : { marginTop: height * 0.1 }) }}>
        <Button
          mode="contained"
          icon="account-plus"
          onPress={() => setInviteModalVisible(true)}
          style={{ alignSelf: 'flex-start', marginBottom: 12 }}
        >
          {t('inviteNewDriver', 'Invite Driver')}
        </Button>
        <Card style={{ marginBottom: 12 }}>
          <Card.Title title={t('filters.title', 'Filters')} />
          <Card.Content>
            <Text variant="labelLarge" style={{ marginBottom: 4 }}>
              {t('filters.search', 'Search')}
            </Text>
            <TextInput
              mode="outlined"
              placeholder={t('search', 'Search by name, email, NIPC, vehicle, or date')}
              value={query}
              onChangeText={(v) => {
                setQuery(v);
                setPage(1);
              }}
              left={<TextInput.Icon icon="magnify" />}
              right={query ? <TextInput.Icon icon="close" onPress={() => {
                setQuery('');
                setPage(1);
              }} /> : undefined}
              style={{ marginBottom: 12 }}
            />

            <Text variant="labelLarge" style={{ marginBottom: 4 }}>
              {t('filters.status', 'Status')}
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Chip
                testID="drivers-filter-all"
                selected={filter === 'all' && coverageFilter !== 'payment_issue'}
                onPress={() => {
                  setFilter('all');
                  setCoverageFilter('all');
                }}
                mode={filter === 'all' && coverageFilter !== 'payment_issue' ? 'flat' : 'outlined'}
                icon={filter === 'all' && coverageFilter !== 'payment_issue' ? 'check' : undefined}
              >
                {t('statusTabs.all', 'All')} ({allCount || totalCount || activeCount + inactiveCount})
              </Chip>
              <Chip
                selected={filter === 'active'}
                onPress={() => {
                  setFilter('active');
                  setCoverageFilter('all');
                }}
                mode={filter === 'active' ? 'flat' : 'outlined'}
                icon={filter === 'active' ? 'check-circle' : undefined}
              >
                {t('statusLabels.active', 'Active')} ({activeCount})
              </Chip>
              {(awaitingCount > 0 || filter === 'awaiting') && (
                <Chip
                  selected={filter === 'awaiting'}
                  onPress={() => {
                    setFilter(filter === 'awaiting' ? 'all' : 'awaiting');
                    setCoverageFilter('all');
                  }}
                  mode={filter === 'awaiting' ? 'flat' : 'outlined'}
                  icon={filter === 'awaiting' ? 'clock-outline' : undefined}
                >
                  {t('statusLabels.awaitingLicense', 'Awaiting license')} ({awaitingCount})
                </Chip>
              )}
              {(licenseIssueCount > 0 || coverageFilter === 'payment_issue') && (
                <Chip
                  testID="drivers-filter-payment-issue"
                  selected={coverageFilter === 'payment_issue'}
                  onPress={() => {
                    setCoverageFilter(coverageFilter === 'payment_issue' ? 'all' : 'payment_issue');
                    setFilter('all');
                  }}
                  mode={coverageFilter === 'payment_issue' ? 'flat' : 'outlined'}
                  icon={coverageFilter === 'payment_issue' ? 'check' : 'credit-card-off-outline'}
                >
                  {t('filters.licensePaymentIssue', 'License payment failed')} ({licenseIssueCount})
                </Chip>
              )}
              <Chip
                selected={filter === 'pending'}
                onPress={() => {
                  setFilter(filter === 'pending' ? 'all' : 'pending');
                  setCoverageFilter('all');
                }}
                mode={filter === 'pending' ? 'flat' : 'outlined'}
                icon={filter === 'pending' ? 'account-clock-outline' : undefined}
              >
                {t('statusLabels.pending', 'Pending')} ({pendingCount})
              </Chip>
              <Chip
                selected={filter === 'invited'}
                onPress={() => {
                  const nextFilter = filter === 'invited' ? 'all' : 'invited';
                  setFilter(nextFilter);
                  if (nextFilter === 'invited') setSelectedDriverIds(new Set());
                  setCoverageFilter('all');
                }}
                mode={filter === 'invited' ? 'flat' : 'outlined'}
                icon={filter === 'invited' ? 'check' : 'email-clock-outline'}
              >
                {t('statusLabels.invited', 'Invited')} ({invitedCount})
              </Chip>
              <Chip
                selected={filter === 'deactivated'}
                onPress={() => {
                  setFilter(filter === 'deactivated' ? 'all' : 'deactivated');
                  setCoverageFilter('all');
                }}
                mode={filter === 'deactivated' ? 'flat' : 'outlined'}
                icon={filter === 'deactivated' ? 'account-minus' : undefined}
              >
                {t('statusLabels.deactivated', 'Deactivated')} ({deactivatedCount || 0})
              </Chip>
            </View>

            <Button
              mode="text"
              icon={showAdvancedFilters ? 'chevron-up' : 'tune-variant'}
              onPress={() => setShowAdvancedFilters(previous => !previous)}
              contentStyle={{ justifyContent: 'flex-start' }}
              style={{ alignSelf: 'flex-start', marginBottom: showAdvancedFilters ? 8 : 0 }}
            >
              {showAdvancedFilters
                ? t('filters.showFewer', 'Fewer filters')
                : t('filters.showMore', 'More filters')}
            </Button>

            {showAdvancedFilters && (
              <View testID="drivers-advanced-filters">
                <Text variant="labelLarge" style={{ marginBottom: 4 }}>
                  {t('filters.payer', 'Payer')}
                </Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Chip
                selected={fundingFilter === 'all'}
                onPress={() => setFundingFilter('all')}
                mode={fundingFilter === 'all' ? 'flat' : 'outlined'}
                icon={fundingFilter === 'all' ? 'check' : undefined}
              >
                {t('statusTabs.all', 'All')}
              </Chip>
              <Chip
                selected={fundingFilter === 'company'}
                onPress={() => setFundingFilter(fundingFilter === 'company' ? 'all' : 'company')}
                mode={fundingFilter === 'company' ? 'flat' : 'outlined'}
                icon={fundingFilter === 'company' ? 'check' : 'briefcase'}
              >
                {t('funding.company', 'Company-paid')}
              </Chip>
              <Chip
                selected={fundingFilter === 'self'}
                onPress={() => setFundingFilter(fundingFilter === 'self' ? 'all' : 'self')}
                mode={fundingFilter === 'self' ? 'flat' : 'outlined'}
                icon={fundingFilter === 'self' ? 'check' : 'account-credit-card'}
              >
                {t('funding.self', 'Personal license')}
              </Chip>
            </View>

            {((fundingCounts.ambiguous ?? 0) > 0 || (coverageCounts.not_covered ?? 0) > 0) && (
              <>
                <Text variant="labelLarge" style={{ marginBottom: 4 }}>
                  {t('filters.needsAttention', 'Needs attention')}
                </Text>
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {(fundingCounts.ambiguous ?? 0) > 0 && (
                    <Chip
                      selected={fundingFilter === 'ambiguous'}
                      onPress={() => setFundingFilter(fundingFilter === 'ambiguous' ? 'all' : 'ambiguous')}
                      mode={fundingFilter === 'ambiguous' ? 'flat' : 'outlined'}
                      icon={fundingFilter === 'ambiguous' ? 'check' : 'help-circle-outline'}
                    >
                      {t('filters.dataToConfirm', 'Data to confirm')} ({fundingCounts.ambiguous})
                    </Chip>
                  )}
                  {(coverageCounts.not_covered ?? 0) > 0 && (
                    <Chip
                      selected={coverageFilter === 'not_covered'}
                      onPress={() => {
                        setCoverageFilter(coverageFilter === 'not_covered' ? 'all' : 'not_covered');
                        setFilter('all');
                      }}
                      mode={coverageFilter === 'not_covered' ? 'flat' : 'outlined'}
                      icon={coverageFilter === 'not_covered' ? 'check' : 'shield-off-outline'}
                    >
                      {t('filters.noActiveLicense', 'No active license')} ({coverageCounts.not_covered})
                    </Chip>
                  )}
                </View>
              </>
            )}

            <Text variant="labelLarge" style={{ marginBottom: 4 }}>
              {t('filters.visibility', 'Visibility')}
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
              <Checkbox
                testID="drivers-include-hidden"
                status={includeHidden ? 'checked' : 'unchecked'}
                onPress={() => setIncludeHidden(!includeHidden)}
              />
              <Text variant="bodyMedium">
                {t('filters.showHidden', 'Show hidden drivers')}
              </Text>
            </View>

            <Text variant="labelLarge" style={{ marginBottom: 4 }}>
              {t('filters.sort', 'Sort by')}
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              <Chip
                selected={sortBy === 'registered_at'}
                mode={sortBy === 'registered_at' ? 'flat' : 'outlined'}
                onPress={() => {
                  if (sortBy === 'registered_at') {
                    setOrderDesc(!orderDesc);
                  } else {
                    setSortBy('registered_at');
                    setOrderDesc(true);
                  }
                }}
                icon={
                  orderDesc
                    ? 'sort-calendar-descending'
                    : 'sort-calendar-ascending'
                }
              >
                {sortBy === 'registered_at'
                  ? orderDesc
                    ? t('newest', 'Newest')
                    : t('oldest', 'Oldest')
                  : t('sortByDate', 'Date')}
              </Chip>
              <Chip
                selected={sortBy === 'name'}
                mode={sortBy === 'name' ? 'flat' : 'outlined'}
                onPress={() => {
                  if (sortBy === 'name') {
                    setOrderDesc(!orderDesc);
                  } else {
                    setSortBy('name');
                    setOrderDesc(false);
                  }
                }}
                icon={
                  orderDesc
                    ? 'sort-alphabetical-descending'
                    : 'sort-alphabetical-ascending'
                }
              >
                {sortBy === 'name'
                  ? orderDesc
                    ? t('za', 'Z-A')
                    : t('az', 'A-Z')
                  : t('sortByName', 'Name')}
              </Chip>
            </View>
              </View>
            )}
          </Card.Content>
        </Card>

        {invitationActionError && (
          <Text accessibilityRole="alert" style={{ color: theme.colors.error, marginBottom: 12 }}>
            {invitationActionError}
          </Text>
        )}

        {/* Bulk Actions Bar */}
        {selectedDriverIds.size > 0 && (
          <Card style={{ marginBottom: 12, backgroundColor: theme.colors.primaryContainer }}>
            <Card.Content>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <Checkbox
                    status={selectedDriverIds.size === drivers.filter(driver => driver.type !== 'invitation').length ? 'checked' : 'indeterminate'}
                    onPress={toggleSelectAll}
                  />
                  <Text variant="bodyLarge">
                    {t('bulkActions.selected', 'Selected: {{count}}', { count: selectedDriverIds.size })}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Button
                    mode="outlined"
                    onPress={handleBulkActivate}
                    loading={bulkActivating}
                    disabled={bulkActivating || bulkTransferring}
                    icon="check-circle"
                  >
                    {t('bulkActions.activate', 'Activate')}
                  </Button>
                  <Button
                    mode="contained"
                    onPress={() => setTransferModalVisible(true)}
                    loading={bulkTransferring}
                    disabled={bulkActivating || bulkTransferring}
                    icon="swap-horizontal"
                  >
                    {t('bulkActions.transfer', 'Transfer')}
                  </Button>
                  <Button
                    mode="text"
                    onPress={clearSelection}
                    icon="close"
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </View>
              </View>
            </Card.Content>
          </Card>
        )}

        <Card>
          <Card.Content>
            {loading ? (
              <ActivityIndicator />
            ) : drivers.length === 0 ? (
              <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('noResults','No results')}</Text>
            ) : (
              <List.Section>
                {filter !== 'invited' && drivers.some(driver => driver.type !== 'invitation') && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 16 }}>
                    <Checkbox
                      status={
                        selectedDriverIds.size > 0 &&
                        drivers.filter(d => d.type !== 'invitation').every(d => selectedDriverIds.has(d.id)) &&
                        (selectedDriverIds.size === Math.max(0, totalCount - invitedCount) || selectedDriverIds.size > totalCount)
                          ? 'checked'
                          : selectedDriverIds.size > 0
                            ? 'indeterminate'
                            : 'unchecked'
                      }
                      onPress={toggleSelectAll}
                    />
                    <Text variant="bodyMedium" style={{ marginLeft: 8 }}>
                      {t('bulkActions.selectAll', 'Select All')}
                      {selectedDriverIds.size > 0 && ` (${selectedDriverIds.size}${totalCount > 0 && totalCount !== selectedDriverIds.size ? ` / ${totalCount}` : ''})`}
                    </Text>
                  </View>
                )}
                {drivers.map(d => {
                  if (d.type === 'invitation') {
                    const busy = invitationActionLoading === d.token;
                    return (
                      <List.Item
                        key={`invitation-${d.token || d.id}`}
                        title={d.name || d.email}
                        description={
                          <View>
                            {d.name !== d.email && <Text style={{ color: theme.colors.onSurfaceVariant }}>{d.email}</Text>}
                            {d.phone_number && <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{d.phone_number}</Text>}
                            {d.license_number && (
                              <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                                {t('driverDetails.licenseNumber', 'License')}: {d.license_number}
                              </Text>
                            )}
                            {d.expires_at && (
                              <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                                {t('expiresAt', 'Expires at')}: {new Date(d.expires_at).toLocaleString()}
                              </Text>
                            )}
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4, alignItems: 'center' }}>
                              <Chip compact icon="email-clock-outline">{t('statusLabels.invited', 'Invited')}</Chip>
                              <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                                {d.license_payer === 'self' ? t('funding.self', 'Personal license') : t('funding.company', 'Company-paid')}
                              </Text>
                            </View>
                          </View>
                        }
                        left={() => <List.Icon icon="email-outline" color={theme.dark ? theme.colors.onTertiaryContainer : theme.colors.tertiary} />}
                        right={() => d.token ? (
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', maxWidth: 180 }}>
                            <Button compact mode="text" disabled={busy} loading={busy} onPress={async () => {
                              setInvitationActionLoading(d.token || null);
                              setInvitationActionError(null);
                              try {
                                const companyId = await getActiveCompanyContextId();
                                const response = await resendDriverInvitation(d.token!, companyId ?? undefined);
                                if (!response.ok) throw new Error(response.data?.detail || t('errorResendingInvitation', 'Error resending invitation'));
                                await fetchDrivers(true);
                              } catch (error) {
                                setInvitationActionError(error instanceof Error ? error.message : t('errorResendingInvitation', 'Error resending invitation'));
                              } finally {
                                setInvitationActionLoading(null);
                              }
                            }}>{t('resend', 'Resend')}</Button>
                            <Button compact mode="text" disabled={busy} textColor={theme.colors.error} onPress={async () => {
                              setInvitationActionLoading(d.token || null);
                              setInvitationActionError(null);
                              try {
                                const companyId = await getActiveCompanyContextId();
                                const response = await cancelDriverInvitation(d.token!, companyId ?? undefined);
                                if (!response.ok) throw new Error(response.data?.detail || t('errorCancelingInvitation', 'Error canceling invitation'));
                                await fetchDrivers(true);
                              } catch (error) {
                                setInvitationActionError(error instanceof Error ? error.message : t('errorCancelingInvitation', 'Error canceling invitation'));
                              } finally {
                                setInvitationActionLoading(null);
                              }
                            }}>{t('cancel', 'Cancel')}</Button>
                          </View>
                        ) : null}
                      />
                    );
                  }
                  const getStatusColor = () => {
                    if (d.relationship_status === 'deactivated') {
                      return theme.colors.error;
                    }
                    if (d.funding?.coverage_status === 'active') {
                      return theme.colors.primary;
                    }
                    if (d.funding?.coverage_status === 'awaiting') {
                      return theme.colors.error;
                    }
                    if (d.relationship_status === 'associated' || d.relationship_status === 'pending') {
                      return theme.dark ? theme.colors.onTertiaryContainer : theme.colors.tertiary;
                    }
                    if (d.status === 'active') {
                      return theme.colors.primary;
                    } else if (d.has_ever_been_activated) {
                      return theme.colors.error;
                    } else {
                      return theme.colors.outline;
                    }
                  };

                  const formatDate = (dateString?: string) => {
                    if (!dateString) return '-';
                    try {
                      const date = new Date(dateString);
                      // Format as DD/MM/YYYY (European format)
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    } catch {
                      return dateString;
                    }
                  };

                  return (
                    <List.Item
                      key={d.id}
                      title={d.name}
                      description={
                        <View>
                          <Text style={{ color: theme.colors.onSurfaceVariant, fontSize: 14 }}>
                            {d.email}
                          </Text>
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 4, gap: 8 }}>
                            <Text style={{ color: theme.colors.onSurfaceVariant, fontSize: 12 }}>
                              📅 {formatDate(d.registered_at)}
                            </Text>
                            {d.activated_at && (
                              <Text style={{ color: theme.colors.primary, fontSize: 12 }}>
                                ✓ {formatDate(d.activated_at)}
                              </Text>
                            )}
                            {d.deactivated_at && (
                              <Text style={{ color: theme.colors.error, fontSize: 12 }}>
                                ✗ {formatDate(d.deactivated_at)}
                              </Text>
                            )}
                            {d.vehicles.length > 0 && (
                              <Text style={{ color: theme.colors.onSurfaceVariant, fontSize: 12 }}>
                                🚗 {d.vehicles.map(v => v.registration_number).join(', ')}
                              </Text>
                            )}
                            {d.report_hidden && (
                              <Chip compact mode="outlined" icon="eye-off" textStyle={{ fontSize: 10 }}>
                                {t('funding.hidden', 'Oculto')}
                              </Chip>
                            )}
                          </View>
                          <DriverFundingSummary
                            funding={d.funding}
                            relationshipStatus={d.relationship_status}
                            driverName={d.name}
                            compact
                            testID={`driver-funding-summary-${d.id}`}
                          />
                        </View>
                      }
                      left={props => (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Checkbox
                            status={selectedDriverIds.has(d.id) ? 'checked' : 'unchecked'}
                            onPress={() => toggleDriverSelection(d.id)}
                          />
                          {isLargeScreen && (
                            <List.Icon
                              {...props}
                              icon={d.status === 'active' ? 'account-check' : (d.has_ever_been_activated ? 'account-minus' : 'account-clock')}
                              color={getStatusColor()}
                            />
                          )}
                        </View>
                      )}
                      onPress={() => router.push(`/dashboard/drivers/${d.id}${includeHidden ? '?include_hidden=true' : ''}`)}
                    />
                  );
                })}
              </List.Section>
            )}
          </Card.Content>
        </Card>

        {totalPages > 1 && (
          <Card style={{ marginTop: 12 }}>
            <Card.Content>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Button 
                  mode="outlined" 
                  onPress={() => setPage(Math.max(1, page - 1))} 
                  disabled={page <= 1}
                  icon="chevron-left"
                >
                  {t('previous', 'Previous')}
                </Button>
                <Text style={{ minWidth: 60, textAlign: 'center' }}>
                  {t('page', 'Page')} {page} {t('of', 'of')} {totalPages}
                </Text>
                <Button 
                  mode="outlined" 
                  onPress={() => setPage(Math.min(totalPages, page + 1))} 
                  disabled={page >= totalPages}
                  icon="chevron-right"
                  contentStyle={{ flexDirection: 'row-reverse' }}
                >
                  {t('next', 'Next')}
                </Button>
              </View>
              {totalCount > 0 && (
                <Text style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: theme.colors.onSurfaceVariant }}>
                  {t('showing', 'Showing')} {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} {t('of', 'of')} {totalCount} {t('drivers', 'drivers')}
                </Text>
              )}
            </Card.Content>
          </Card>
        )}
      </ScrollView>
      {!isLargeScreen && (
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
      )}
      </Surface>

      <InviteDriverModal
        visible={inviteModalVisible}
        onDismiss={() => setInviteModalVisible(false)}
        onSuccess={() => {
          setInviteModalVisible(false);
          fetchDrivers();
        }}
      />

      {/* Transfer Modal */}
      <TransferModal
        visible={transferModalVisible}
        onDismiss={() => setTransferModalVisible(false)}
        onTransfer={handleBulkTransfer}
        title={t('bulkActions.transferDrivers', 'Transfer {{count}} Drivers', { count: selectedDriverIds.size })}
        description={t('bulkActions.transferDriversDescription', 'Select the target company to transfer the selected drivers to.')}
        transferRelatedVehicles={true}
        relatedItemsCount={getRelatedVehiclesCount()}
        itemCount={selectedDriverIds.size}
      />

      {/* Result Dialog */}
      <Portal>
        <Dialog 
          visible={resultDialogVisible} 
          onDismiss={() => {
            setResultDialogVisible(false);
            setHasLicenseError(false);
          }}
          style={isLargeScreen ? { maxWidth: 600, alignSelf: 'center', width: '90%' } : undefined}
        >
          <Dialog.Title>{t('bulkActions.result', 'Operation Result')}</Dialog.Title>
          <Dialog.Content>
            <Text>{resultMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            {hasLicenseError && (
              <Button 
                onPress={() => {
                  setResultDialogVisible(false);
                  setHasLicenseError(false);
                  router.push('/dashboard/manage-licenses');
                }}
                mode="contained"
                icon="license"
              >
                {t('bulkActions.goToLicenses', 'Go to Licenses')}
              </Button>
            )}
            <Button onPress={() => {
              setResultDialogVisible(false);
              setHasLicenseError(false);
            }}>
              {t('common.ok', 'OK')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}


